import React from 'react';
import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { FileText } from 'lucide-react';
import type { IApplicationCardProps } from '../../interfaces';
import { cleanJobTitle, formatShortDate, getApplicationStatus } from '../functions';

const STATUS_CHIP_COLOR: Record<string, 'info' | 'error' | 'secondary' | 'primary' | 'default'> = {
  'Applied': 'primary',
  'Not Selected': 'error',
  'Offered': 'secondary',
};

export const ApplicationCard = ({ application }: IApplicationCardProps) => {
  const { label } = getApplicationStatus(application.group);
  const jobTitle = cleanJobTitle(application.jobTitle);
  const chipColor = STATUS_CHIP_COLOR[application.group ?? ''] ?? 'default';

  return (
    <Card component="article" sx={{ height: '100%' }}>
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: 2.5,
          height: '100%',
          boxSizing: 'border-box',
          '&:last-child': { pb: 2.5 },
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (theme) => alpha(theme.palette.success.main, 0.1),
              color: 'success.main',
              flexShrink: 0,
            }}
          >
            <FileText size={20} aria-hidden="true" />
          </Box>
          <Chip label={label} size="small" color={chipColor} sx={{ fontWeight: 600 }} />
        </Stack>

        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
            {jobTitle}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Applied {formatShortDate(application.createdAt)}
          </Typography>
        </Box>

        <Box
          sx={{
            pt: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Application ID
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontFamily: 'monospace',
              color: 'text.secondary',
              maxWidth: 140,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {application.jobApplicantId}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};
